import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader';
import Reveal from '@/components/common/Reveal';
import BrowserFrame from '@/components/common/BrowserFrame';
import { projects } from '@/data/projects';
import { buttonVariants } from '@/components/ui/button';


export default function Work() {
  const list = projects.filter(p => !p.placeholder);

  return (
    <motion.main
      id="main-content"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>Work | TechsFlow</title>
      </Helmet>
      <PageHeader
        eyebrow="Index · 01 · The Work"
        title="A public shelf"
        italicTail="of everything we've shipped."
        lede={`${list.length} live projects, one page. Every entry links to a real URL — no dead moodboards, no concepts that never launched.`}
      />

      {/* Projects list */}
      <section className="py-14 md:py-20">
        <div className="mx-auto container-editorial">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-16 md:space-y-24"
          >
            {list.map((p, i) => (
              <CaseRow key={p.slug} p={p} idx={i} />
            ))}
          </motion.div>
        </div>
      </section>
    </motion.main>
  );
}

const CaseRow = ({ p, idx }) => {
  const reverse = idx % 2 === 1;
  return (
    <Reveal>
      <article className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
        <a
          href={p.url || '#'}
          target={p.url ? '_blank' : undefined}
          rel="noreferrer"
          className={`lg:col-span-7 group ${reverse ? 'lg:order-2' : ''}`}
        >
          <div className="relative">
            <BrowserFrame src={p.image} alt={p.name} aspect="aspect-[16/10]" url={p.url} />
            {p.placeholder && (
              <div className="absolute inset-0 bg-ink/55 backdrop-blur-[2px] flex items-center justify-center rounded-[1.25rem]">
                <span className="font-mono text-xs text-paper uppercase tracking-widest border border-paper/30 px-3 py-1.5 rounded-full">
                  In progress · reserved
                </span>
              </div>
            )}
            <div className="label-eyebrow flex items-center justify-between mt-4">
              <span>No. {String(idx + 1).padStart(2, '0')} / {p.location}</span>
              <span className="md:hidden"><ArrowUpRight className="w-4 h-4" /></span>
            </div>
          </div>
        </a>

        <div className={`lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}>
          <div className="label-eyebrow mb-4 flex items-center gap-3">
            <span>{p.industry} · {p.location}</span>
            {p.concept && (
              <span className="px-2 py-0.5 rounded-sm bg-secondary text-sienna border border-border/50">Concept</span>
            )}
          </div>
          <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-balance mb-5">
            {p.name}
          </h3>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
            {p.description}
          </p>

          <div className="grid grid-cols-2 gap-5 mb-8">
            <div>
              <div className="label-eyebrow mb-2">Scope</div>
              <ul className="space-y-1 text-sm">
                {p.scope.map(s => <li key={s}>{s}</li>)}
              </ul>
            </div>
          </div>

          {p.metrics && (
            <div className="flex items-center gap-8 mb-8">
              {p.metrics.map(m => (
                <div key={m.label}>
                  <div className="font-serif text-3xl md:text-4xl leading-none">{m.value}</div>
                  <div className="text-xs text-muted-foreground mt-1.5">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mb-8">
            {p.palette.map(c => (
              <span key={c} className="w-6 h-6 rounded-full border border-border" style={{ background: c }} />
            ))}
          </div>

          {p.url ? (
            <a href={p.url} target="_blank" rel="noreferrer" className={buttonVariants({ variant: 'editorial' })}>
              Visit the site <ArrowUpRight className="w-4 h-4" />
            </a>
          ) : (
            <span className={buttonVariants({ variant: 'ghost-editorial', className: 'cursor-not-allowed opacity-70' })}>
              In build — coming soon
            </span>
          )}
        </div>
      </article>
    </Reveal>
  );
};
